/**
 * ScrollableList.jsx
 * Created by Madhusudan Ravi on 5/2/2019.
 *
 * Note that role="list" and role="listitem" are used because some screen readers
 * will not detect a list properly if the list styling is removed.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Heading from '../Heading';
import ScrollableListItem from './ScrollableListItem';

export default class ScrollableList extends React.Component {
    static propTypes = {
        /** Heading displayed above the list */
        title: PropTypes.string,

        /** Heading tag to use (h1, h2, etc.) */
        headingLevel: PropTypes.number,

        /** Heading appearance */
        headingAppearance: PropTypes.oneOf(['display', 'h1', 'h2', 'h3', 'h4', 'h5', 'h5Caps', 'h6']),

        /** One or more `ScrollableListItem`s. If using children, do not use items */
        children: PropTypes.node,

        /** An array of list items to render. If using items, do not use children. */
        items: PropTypes.array,

        /** The maximum height in pixels the list box should be. Set to 'none'
         * to make the list its natural height  */
        maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['none'])]),

        /** Appearance of the list: numbers, bullets or none */
        listType: PropTypes.oneOf(['ol', 'ul', 'none']),

        /** Number of columns (1-4) the list should be displayed in */
        cols: PropTypes.number,

        /** Additional class added to the outer container */
        className: PropTypes.string
    }

    static defaultProps = {
        title: undefined,
        cols: 1,
        listType: 'none',
        headingLevel: 3,
        headingAppearance: "h6",
        maxHeight: 170,
        items: undefined
    }

    renderListItems = () => {
        const { items, children } = this.props;

        if (items && items.length) {
            return items.map((item, index) => (
                <ScrollableListItem key={index}>{item}</ScrollableListItem>
            ));
        }

        return children;
    }

    render() {
        const {
            title,
            listType,
            children,
            headingAppearance,
            headingLevel,
            maxHeight,
            className,
            ...rest
        } = this.props;

        const ListElement = listType === 'ol' ? 'ol' : 'ul';
        let maxHeightComputed = maxHeight;

        if (typeof maxHeight === 'number') {
            maxHeightComputed = maxHeight / 10;
        }

        return (
            <div
                data-component="ScrollableList"
                className={cx('sarsa-scrollable-list', className)}
                {...rest}>
                {title && (
                    <Heading
                        appearance={headingAppearance}
                        headingLevel={headingLevel}>
                        {title}
                    </Heading>
                )}
                <div
                    className="sarsa-scrollable-list-wrapper"
                    style={{ maxHeight: `${maxHeightComputed}rem` }} >
                    <ListElement
                        role="list"
                        className={cx(`cols-${this.props.cols}`, {
                            undecorated: listType === 'none'
                        })}>
                        {this.renderListItems()}
                    </ListElement>
                </div>
            </div>
        );
    }
}
